import { FC, useCallback, MouseEvent, KeyboardEvent, useMemo } from "react";
import { MenuListProps } from "react-select";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { Body, Paragraph } from "capitalrx-components-2.0/lib/typography";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Textbox } from "../../../Textbox";
import { Button, Icon } from "capitalrx-components-2.0/lib/general";
import { OptionItem } from "../../OptionItem";
import { CustomReactSelectProps } from "../../CustomReactSelectProps";
import { Checkbox } from "../../../Checkbox";

/**
 * The menu list component.
 */
export const MenuList: FC<MenuListProps<OptionItem | null>> = (props) => {
  const { children, selectProps, innerProps, innerRef } = props;
  const {
    searchable = false,
    searchInputProps = {},
    searchRef,
    searchValue,
    enableCheckboxOptions,
    selectedValues,
    options,
    menuIsOpen,
    showMasterCheck,
    onMenuClose,
    onSearchChange,
    onSearchBlur,
    onSelectAll } = selectProps as CustomReactSelectProps;

  const menuListClassName = classNames(styles.MenuList, "menu-list items");

  const itemsCount = useMemo(() => {
    if (!selectedValues) return 0;
    return Array.from(selectedValues).length;
  }, [selectedValues]);

  const mouseDownHandler = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const keyDownHandler = useCallback((ev: KeyboardEvent) => {
    const { key } = ev;

    if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === "Return" || key === "Escape") {
      ev.preventDefault();
      return;
    }
    if (key === "Tab" && menuIsOpen && searchRef?.current) {
      // TODO: previous requirement was close on Tab key, but this is not accessible. Need to revisit.
      onMenuClose?.();
    }
    ev.stopPropagation();
  }, []);

  const checkboxValue = useMemo(() => {
    if (!selectedValues) return false;
    if (selectedValues.size === options?.length) return true;
    if (selectedValues.size > 0 && selectedValues.size <= itemsCount) return "indeterminate";
  }, [selectedValues]);

  const clearHandler = useCallback(() => {
    if (!onSearchChange || !searchRef) return;
    onSearchChange(null, null);
    searchRef.current && searchRef.current.focus();
  }, [searchRef]);

  return (
    <Body size="small">
      <Stack spacing="none">
        {searchable && (
          <div className={classNames(styles.SearchBox, "searchbox", { clearable: enableCheckboxOptions })}>
            <div onKeyDown={keyDownHandler} onMouseDown={mouseDownHandler} onBlur={onSearchBlur}>
                <Stack>
                  <Textbox
                    ref={searchRef}
                    autoFocus
                    value={searchValue}
                    onChange={onSearchChange}
                    prefix={<Icon name="search" color="darkgrey" />}
                    hideInstructions
                    hideLabel
                    placeholder="Search..."
                    {...searchInputProps}
                  />
                  {
                    enableCheckboxOptions && searchValue && !!searchValue.length && (
                      // TODO: figure out how to make clear button accessible via tab key
                      <Button
                        className={classNames(styles.DropdownSearchClearButton, "dropdown-search-clear-button")}
                        icon="x"
                        size="small"
                        appearance="text"
                        onClick={clearHandler}
                        aria-label="Clear Search"
                      />
                    )
                  }
                </Stack>
            </div>
          </div>
        )}
        {
          enableCheckboxOptions && showMasterCheck && (
            <Shelf spacing="medium" className={styles.MenuListCheckbox}>
              <Checkbox
                value={checkboxValue}
                onChange={onSelectAll}
                unpadded
                hideLabel
              />
              <Paragraph>All items</Paragraph>
              <Shelf.Spacer/>
            </Shelf>
          )
        }
        <div {...innerProps} ref={innerRef} className={menuListClassName} role="listbox">
          {children}
        </div>
      </Stack>
      {
        enableCheckboxOptions && (
          <Shelf spacing="none" className={styles.MenuListFooter}>
            <Paragraph>{itemsCount || 0} selected</Paragraph>
            <Shelf.Spacer />
          </Shelf>
        )
      }
    </Body>
  );
};
